/**
 * Tolkar ett inklistrat koordinatpar, till exempel "57.89032, 12.69060" som
 * man får när man kopierar en position från hitta.se eller Google Maps.
 *
 * Varför modulen finns: att skriva in latitud och longitud var för sig är
 * felstavningens bästa vän, och operatören har redan paret i urklipp. Att
 * klistra in det i första rutan och få det uppdelat är en handgrepp mindre på
 * exakt de adresser som redan kräver handpåläggning.
 *
 * ⚠️ Funktionen vägrar hellre än gissar. Returnerar den null ska anroparen
 * INTE ingripa — då får webbläsaren klistra in som vanligt, så att den som
 * kopierar ett enda tal fortfarande kan göra det, oavsett om talet skrivs med
 * punkt eller svensk decimalkomma. En tolkning som chansar på en tvetydig
 * sträng skriver tyst fel position, och en fel position som ser rätt ut är
 * hela GF-34:s felklass.
 *
 * REGLERNA, beslutade 2026-09-09:
 *
 * 1. Separator och decimaltecken. Punkt som decimal med komma som avdelare är
 *    normalfallet ("57.89032, 12.69060"). Svenska källor kan ge komma som
 *    decimal ("57,89032 12,69060"). Båda stöds, och strukturen avgör vilket
 *    som är vilket: finns blanksteg eller semikolon delar vi på det, och då är
 *    komma alltid decimal. Finns bara komma delar vi på det, och då är punkt
 *    decimal. Blir det inte exakt två tal vägrar vi.
 * 2. Ordningen. Latitud först, som båda källorna ger. Ser paret omkastat ut
 *    för svenska förhållanden rapporteras det som `swapped` — vi rättar det
 *    INTE åt användaren, eftersom vi inte kan veta vad hen menade.
 * 3. Grader, minuter och sekunder stöds inte. Allt som innehåller bokstäver
 *    eller gradtecken avvisas i stället för att tolkas till hälften.
 */

// Sverige, grovt tilltaget. Används bara för att känna igen omkastade par,
// aldrig för att avvisa en koordinat — GeoFix ska kunna visa en adress var
// som helst, det är just det GF-34-familjen handlar om.
const SVERIGE_LAT = [54, 70];
const SVERIGE_LNG = [10, 25];

const inom = ([min, max], v) => v >= min && v <= max;

/** Ett rent tal: valfritt tecken, siffror, valfri decimaldel. Inget annat. */
const TAL = /^[+-]?\d+(\.\d+)?$/;

/**
 * Längdspärr. Inget giltigt koordinatpar är i närheten så långt, och den
 * stänger samtidigt frågan om hur mönstren beter sig på en väldigt lång
 * inklistring. Billigare att aldrig ta reda på det.
 */
const MAX_LANGD = 200;

/**
 * @returns {{lat:number, lng:number, swapped:boolean}|null}
 */
export function parseCoordinatePair(text) {
  if (typeof text !== 'string') {
    return null;
  }
  const rensad = text.trim();
  if (!rensad || rensad.length > MAX_LANGD) {
    return null;
  }
  // Regel 3: bokstäver, gradtecken och väderstreck betyder ett format vi inte
  // tolkar. Avvisa hellre än att tolka halva strängen.
  if (/[^\d\s.,;+-]/.test(rensad)) {
    return null;
  }

  let delar;
  if (/[\s;]/.test(rensad)) {
    // Blanksteg eller semikolon delar paret. Då är komma decimaltecken.
    // Det avslutande kommat strippas för formen "57,89032, 12,69060", där
    // kommat är BÅDE decimal och avdelare — annars faller den på TAL.
    delar = rensad
      .split(/[\s;]+/)
      .map((d) => d.replace(/,$/, '').replace(',', '.'))
      .filter((d) => d.length > 0);
  } else {
    // Bara komma kvar som möjlig avdelare, alltså formen "57.89032,12.69060".
    //
    // ⚠️ Kravet på en punkt är det som skiljer ett par från ETT svenskt tal.
    // Utan det blir "57,89" till latitud 57 och longitud 89, alltså Kina,
    // tyst och utan varning — och 89 ligger utanför de svenska intervallen så
    // omkastningskontrollen fångar det inte heller. Det är projektets egen
    // felklass: en position som ser rätt ut men pekar fel (GF-34, GF-39).
    // Finns ingen punkt är strängen tvetydig, och tvetydigt ska ge null.
    if (!rensad.includes('.')) {
      return null;
    }
    delar = rensad.split(',').map((d) => d.trim()).filter((d) => d.length > 0);
  }

  if (delar.length !== 2 || !delar.every((d) => TAL.test(d))) {
    return null;
  }

  const lat = Number(delar[0]);
  const lng = Number(delar[1]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }
  // Utanför jordklotet är det inte ett koordinatpar, oavsett hur det ser ut.
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
    return null;
  }

  // Regel 2: paret ser omkastat ut om det INTE går ihop som svenskt par men
  // gör det om man byter plats på talen.
  const somAngivet = inom(SVERIGE_LAT, lat) && inom(SVERIGE_LNG, lng);
  const omkastat = inom(SVERIGE_LAT, lng) && inom(SVERIGE_LNG, lat);

  return { lat, lng, swapped: !somAngivet && omkastat };
}
